import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import { Formik, Form } from "formik";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import moment from "moment";
import FormField from "components/FormField";
import * as Yup from "yup";
import { endOfMonth } from "date-fns";
import DatePickerField from "components/DatePickerField";
import { toast } from "react-toastify";

const DataSchema = Yup.object().shape({
  orgId: Yup.string(),
  locationId: Yup.string(),
  date: Yup.string().required("Date is required"),
});

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function Aggregation() {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const query = useQuery();
  const type = query.get("type");

  const isShelterLuv = type === "shelterluv" || !type;

  const config = useMemo(() => {
    const baseConfig = {
      shelterluv: {
        title: "ShelterLuv Aggregation",
        endpoint: "/api/v1/shelterluv_aggregations",
      },
      shelterbuddy: {
        title: "ShelterBuddy Aggregation",
        endpoint: "/api/v1/shelterbuddy_aggregations",
      },
    };
    return baseConfig[type] || baseConfig.shelterluv;
  }, [type]);

  const { mutate: aggregateData, isLoading } = useMutation(
    ({ orgId, locationId, date }) =>
      apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: config.endpoint,
        body: {
          orgId: orgId ? orgId.trim() : null,
          locationId: locationId ? locationId.trim() : null,
          month: moment(date).format("M"),
          year: moment(date).format("YYYY"),
        },
      }),
    {
      onSuccess: (_data, { resetForm }) => {
        toast.success(`Aggregation job has been queued successfully`, {
          autoClose: 3000,
        });
        resetForm();
      },
      onError: (error) => {
        toast.error(() => formatUiErrors(error), {
          autoClose: 5000,
          className: "toast-center",
        });
      },
    }
  );

  return (
    <>
      <div className="p-5">
        <h1>{config.title}</h1>
      </div>

      <div className="bg-white rounded p-6 border">
        <Formik
          key={type}
          initialValues={{ orgId: "", locationId: "", date: "" }}
          validationSchema={DataSchema}
          onSubmit={(values, { resetForm }) => {
            aggregateData({ ...values, resetForm });
          }}
        >
          {({ isValid, dirty }) => (
            <Form>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <FormField
                    name="orgId"
                    label="Org ID"
                    tooltip={
                      <div>
                        Type a single OrgID to aggregate data for a specific
                        organization. Leave blank to aggregate data for all
                        organizations.
                      </div>
                    }
                  />
                </div>
                {isShelterLuv && (
                  <div>
                    <FormField
                      name="locationId"
                      label="Location ID"
                      tooltip={
                        <div>
                          Type a LocationID to aggregate data for a specific
                          location. Leave blank to aggregate for all locations
                          in the organization.
                        </div>
                      }
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <DatePickerField
                  label="Month/Year"
                  name="date"
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  className="form-input w-full"
                  maxDate={endOfMonth(new Date())}
                  required
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={!isValid || !dirty}
                  isLoading={isLoading}
                  loadingText={<>Aggregating Data&hellip;</>}
                >
                  Aggregate Data
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default Aggregation;
